import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  DataContextBuilder,
  DataSourceChangeEvent,
  ElderBasicPaneLayoutComponent,
  ElderChipsModule,
  ElderPaneComponent,
  ElderPipesModule,
  ElderSearchModule,
  ElderTableModule,
  Filter,
  IDataContext,
  Sort,
  UnreachableCaseError,
} from '@elderbyte/ngx-starter'
import { Job, JobKind, JobState } from '../job'
import { Router } from '@angular/router'
import { JobService } from '../job.service'
import { ThemePalette } from '@angular/material/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { DatePipe } from '@angular/common'
import { MatIcon } from '@angular/material/icon'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatTooltip } from '@angular/material/tooltip'
import { combineLatestWith, interval, Observable, switchMap, throttleTime } from 'rxjs'
import { filter, map, tap } from 'rxjs/operators'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { JobPriorityIndicatorComponent } from '../job-priority-indicator/job-priority-indicator.component'
import { JobSpecViewComponent } from '../job-spec-view/job-spec-view.component'

@Component({
  selector: 'app-job-browser',
  templateUrl: './job-browser.component.html',
  styleUrls: ['./job-browser.component.scss'],
  imports: [
    ElderTableModule,
    MatSort,
    ElderSearchModule,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatIcon,
    ElderChipsModule,
    DatePipe,
    ElderTranslatePipe,
    ElderPipesModule,
    MatProgressSpinner,
    MatTooltip,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    JobPriorityIndicatorComponent,
    JobSpecViewComponent,
  ],
})
export class JobBrowserComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)
  public readonly data: IDataContext<Job>

  public readonly jobKind = input.required<JobKind>()
  public readonly parentJobId = input<string>(undefined)

  public readonly forcedFilters = computed(() => {
    const kind = this.jobKind()
    const parentJobId = this.parentJobId()
    return [new Filter('kind', kind), new Filter('parentJobId', parentJobId)]
  })

  public readonly openJobDetail = output<Job>()

  protected readonly JobState = JobState

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor(
    private jobService: JobService,
    private router: Router
  ) {
    this.data = DataContextBuilder.start<Job>()
      .pageSize(30)
      .reloadOnLocalChanges()
      .build(this.jobService)

    this.data.data
      .pipe(
        takeUntilDestroyed(),
        combineLatestWith(interval(1000)),
        throttleTime(1000),
        map(([jobs, _]) => this.selectJobsToRefresh(jobs)),
        filter((jobsToRefresh) => jobsToRefresh.length > 0),
        switchMap((jobs) => this.refreshJobs(jobs)),
        tap((jobs) => this.patchJobRows(jobs))
      )
      .subscribe({
        error: (err) => this.logger.error('Failed to refresh jobs', err),
      })
  }

  private patchJobRows(jobs: Job[]): void {
    this.jobService.publishChangeEvent(DataSourceChangeEvent.modified(jobs))
  }

  private refreshJobs(jobs: Job[]): Observable<Job[]> {
    return this.jobService.findByIds(jobs.map((j) => j.id))
  }

  private selectJobsToRefresh(jobs: Job[]): Job[] {
    return jobs.filter((j) => this.shouldRefresh(j)).slice(0, 1000) // Max 1000 jobs to refresh at once
  }

  private shouldRefresh(job: Job): boolean {
    switch (job.state) {
      case JobState.FAILED:
      case JobState.SUCCESS:
        return false
      case JobState.WAITING:
      case JobState.RUNNING:
      case JobState.AWAITING_SUBTASKS:
        return true
      default:
        throw new UnreachableCaseError(job.state)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.data.start([new Sort('id', 'desc')])
  }

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public getColor(state: JobState): ThemePalette {
    switch (state) {
      case JobState.SUCCESS:
        return 'primary'
      case JobState.FAILED:
        return 'warn'
      case JobState.RUNNING:
      case JobState.AWAITING_SUBTASKS:
        return 'accent'
      case JobState.WAITING:
        return 'accent'
      default:
        return undefined
    }
  }

  public getIcon(state: JobState): string {
    switch (state) {
      case JobState.SUCCESS:
        return 'assignment_turned_in'
      case JobState.FAILED:
        return 'assignment_late'
      case JobState.RUNNING:
      case JobState.AWAITING_SUBTASKS:
        return 'assignment_ind'
      case JobState.WAITING:
        return 'assignment'
      default:
        return 'assignment'
    }
  }

  public formatProgress(progressPercent: number): string {
    return progressPercent ? progressPercent.toFixed(2) + '%' : '-'
  }

  public openDetail(job: Job): void {
    if (job) {
      this.openJobDetail.emit(job)
    }
  }

  protected coerceJob(templateVariable: unknown): Job {
    return templateVariable as Job
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  /**
   * Reloads the data within the table.
   */
  private reload(): void {
    this.data.reload()
  }
}
